import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Lightweight query that only fetches the 4 fields needed to decide
 * whether onboarding is complete. Does NOT fetch company logo, admin role,
 * or any other header chrome — those are fetched lazily by useHeaderData.
 *
 * This keeps ProtectedRoute from blocking on unnecessary data.
 */
interface OnboardingGateData {
  profileExists: boolean;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  companyId: string | null;
  onboardingComplete: boolean;
}

export const onboardingGateQueryKey = (userId: string | undefined) => ['onboardingGate', userId];

export const onboardingGateQueryFn = async (userId: string): Promise<OnboardingGateData> => {
  const { data } = await supabase
    .from('users')
    .select('id, first_name, last_name, phone, company_id')
    .eq('id', userId)
    .maybeSingle();

  const profileExists = !!data?.id;
  const firstName = data?.first_name || null;
  const lastName = data?.last_name || null;
  const phone = data?.phone || null;
  const companyId = data?.company_id || null;

  return {
    profileExists,
    firstName,
    lastName,
    phone,
    companyId,
    onboardingComplete: !!(firstName && lastName && companyId),
  };
};

export const useOnboardingGate = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: onboardingGateQueryKey(user?.id),
    queryFn: () => onboardingGateQueryFn(user!.id),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
